/**
 * API Client — 统一后端通信层 + 监控埋点
 *
 * 替代 Supabase 客户端，统一调用后端 REST API
 * 后端使用 MySQL 数据库，通过 API 暴露数据操作
 */

import { API_BASE_URL, API_ENDPOINTS } from './config';
import type { ApiMetrics, ApiErrorEntry } from '@/types';

/**
 * 请求配置
 */
interface RequestConfig {
  timeout: number;
  retries: number;
  retryDelay: number;
  slowRequestThreshold: number;
}

const DEFAULT_CONFIG: RequestConfig = {
  timeout: 30000,
  retries: 3,
  retryDelay: 1000,
  slowRequestThreshold: 3000,
};

/**
 * API 监控指标（内存中统计）
 */
const apiMetrics: {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  totalDuration: number;
  slowRequests: number;
  errors: ApiErrorEntry[];
} = {
  totalRequests: 0,
  successfulRequests: 0,
  failedRequests: 0,
  totalDuration: 0,
  slowRequests: 0,
  errors: [],
};

/**
 * 获取 API 监控指标（用于调试面板）
 */
export function getApiMetrics(): ApiMetrics {
  return {
    ...apiMetrics,
    averageDuration: apiMetrics.totalRequests > 0
      ? Math.round(apiMetrics.totalDuration / apiMetrics.totalRequests)
      : 0,
    successRate: apiMetrics.totalRequests > 0
      ? ((apiMetrics.successfulRequests / apiMetrics.totalRequests) * 100).toFixed(1) + '%'
      : '100%',
  };
}

/**
 * 上报 API 监控数据到 Sentry / Analytics
 */
interface MetricReport {
  method: string;
  endpoint: string;
  url: string;
  status: number;
  duration: number;
  success: boolean;
  error: string | null;
  retryCount: number;
}

function reportMetric(metric: MetricReport): void {
  // 仅在开发环境输出到控制台
  if (import.meta.env.DEV) {
    if (metric.status >= 400) {
      console.warn('[API Monitor]', metric.method, metric.endpoint, `(${metric.duration}ms, Status: ${metric.status})`);
    } else {
      console.log('[API Monitor]', metric.method, metric.endpoint, `(${metric.duration}ms)`);
    }
  }

  // 尝试上报到 Sentry
  try {
    const Sentry = (window as unknown as Record<string, unknown>)?.Sentry as {
      startSpan?: (config: Record<string, unknown>, callback: (span: { setData: (key: string, value: unknown) => void; end: () => void } | null) => void) => void;
    };
    if (Sentry?.startSpan && metric.status >= 400) {
      Sentry.startSpan(
        {
          name: `api-error-${metric.method}-${metric.endpoint}`,
          op: 'http.client',
        },
        (span) => {
          if (span) {
            span.setData('endpoint', metric.endpoint);
            span.setData('status', metric.status);
            span.setData('duration', metric.duration);
            span.setData('error', metric.error);
            span.end();
          }
        }
      );
    }
  } catch {
    // Sentry 未初始化时静默失败
  }
}

/**
 * 带超时的 fetch
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeout: number = DEFAULT_CONFIG.timeout
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * API 响应包装
 */
class ApiResponse<T = unknown> {
  data: T;
  status: number;
  message: string;
  ok: boolean;

  constructor(data: T, status: number, message: string = '') {
    this.data = data;
    this.status = status;
    this.message = message;
    this.ok = status >= 200 && status < 300;
  }
}

/**
 * API Client 类
 */
class ApiClient {
  private baseURL: string;
  private headers: Record<string, string>;
  private config: RequestConfig;

  constructor(config: Partial<RequestConfig & { baseURL?: string; headers?: Record<string, string> }> = {}) {
    this.baseURL = config.baseURL || API_BASE_URL;
    this.headers = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * 设置认证 Token
   */
  setAuthToken(token: string | null): void {
    if (token) {
      this.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.headers['Authorization'];
    }
  }

  /**
   * 构建完整 URL
   */
  buildURL(endpoint: string, params: Record<string, unknown> = {}): string {
    const url = new URL(
      endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`
    );

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });

    return url.toString();
  }

  /**
   * 带重试的请求（含监控埋点）
   */
  async request<T = unknown>(
    method: string,
    endpoint: string,
    data: Record<string, unknown> | null = null,
    params: Record<string, unknown> = {},
    retryCount: number = 0
  ): Promise<ApiResponse<T>> {
    const url = this.buildURL(endpoint, params);
    const startTime = performance.now();

    const options: RequestInit = {
      method,
      headers: { ...this.headers },
    };

    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      options.body = JSON.stringify(data);
    }

    // 监控计数
    apiMetrics.totalRequests++;

    try {
      const response = await fetchWithTimeout(url, options, this.config.timeout);
      const responseData = await response.json() as { data?: T; message?: string };

      // 计算请求耗时
      const duration = Math.round(performance.now() - startTime);
      apiMetrics.totalDuration += duration;

      // 判断成功/失败
      const isOk = response.status >= 200 && response.status < 300;
      if (isOk) {
        apiMetrics.successfulRequests++;
      } else {
        apiMetrics.failedRequests++;
      }

      // 慢请求记录
      if (duration > this.config.slowRequestThreshold) {
        apiMetrics.slowRequests++;
      }

      // 上报监控
      reportMetric({
        method,
        endpoint,
        url,
        status: response.status,
        duration,
        success: isOk,
        error: isOk ? null : (responseData.message || 'Unknown error'),
        retryCount,
      });

      // 限制错误缓存大小
      if (!isOk) {
        apiMetrics.errors.push({
          timestamp: Date.now(),
          method,
          endpoint,
          status: response.status,
          message: responseData.message || '',
          duration,
        });
        if (apiMetrics.errors.length > 50) {
          apiMetrics.errors.shift();
        }
      }

      return new ApiResponse<T>(responseData.data as T, response.status, responseData.message || '');
    } catch (error: unknown) {
      const err = error as Error;
      // 计算即使在异常情况下的耗时
      const duration = Math.round(performance.now() - startTime);
      apiMetrics.totalDuration += duration;
      apiMetrics.failedRequests++;

      // 网络错误重试
      if (retryCount < this.config.retries && this.shouldRetry(err)) {
        await this.delay(this.config.retryDelay * (retryCount + 1));
        return this.request<T>(method, endpoint, data, params, retryCount + 1);
      }

      // 上报监控（网络错误）
      reportMetric({
        method,
        endpoint,
        url,
        status: 0,
        duration,
        success: false,
        error: err.message || err.name || 'Network error',
        retryCount,
      });

      // 缓存错误
      apiMetrics.errors.push({
        timestamp: Date.now(),
        method,
        endpoint,
        status: 0,
        message: err.message || 'Network error',
        duration,
      });
      if (apiMetrics.errors.length > 50) {
        apiMetrics.errors.shift();
      }

      throw this.handleError(err);
    }
  }

  /**
   * 判断是否应该重试
   */
  shouldRetry(error: Error): boolean {
    return error.name === 'AbortError' || error.name === 'TypeError';
  }

  /**
   * 延迟函数
   */
  delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 错误处理
   */
  handleError(error: Error): Error {
    if (error.name === 'AbortError') {
      return new Error('请求超时，请稍后重试');
    }
    return error;
  }

  // ====== REST 方法 ======

  get<T = unknown>(endpoint: string, params: Record<string, unknown> = {}): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, null, params);
  }

  post<T = unknown>(endpoint: string, data: Record<string, unknown> = {}): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, data);
  }

  put<T = unknown>(endpoint: string, data: Record<string, unknown> = {}): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, data);
  }

  patch<T = unknown>(endpoint: string, data: Record<string, unknown> = {}): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, data);
  }

  delete<T = unknown>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint);
  }
}

// 导出单例
export const apiClient = new ApiClient();
export default ApiClient;
